import { danaDo, PRAG_ESKALACIJE, PRAG_UPOZORENJA, pragUpozorenja } from "@/lib/status";
import { opisRupe, type Rupa as RupaZaPaznju } from "@/lib/sazetak";
import type { Radnik } from "@/lib/radnici";
import { punoIme } from "@/lib/radnici";

/**
 * "Zahtijeva pažnju": one list, four kinds of deadline, ordered by how soon
 * somebody has to act.
 *
 * Mixing the kinds is the point. Four separate lists would make the person
 * scan four places and decide which is more urgent — the ordering is the work,
 * so the system does it.
 *
 * Sorting is by days remaining, expired first. Anything already blocking work
 * outranks anything merely approaching, whatever the arithmetic says.
 */

export type VrstaPaznje =
  | "dozvola"
  | "lijecnicki"
  | "ugovor"
  | "oprema"
  | "profil"
  /** A day whose hours never arrived — O-18, and the only kind here that
      is about a site rather than a person. */
  | "evidencija";

export interface Stavka {
  radnikId: string;
  radnik: string;
  vrsta: VrstaPaznje;
  /** What is wrong, in one line, in Croatian. */
  poruka: string;
  /** Negative when the deadline has passed. Null when there is no date. */
  dana: number | null;
  /** Blocks work today. These are shown first and coloured. */
  blokira: boolean;
  /** Where the verb goes. Defaults to the worker's card. */
  poveznica?: string;
  /** The verb itself, written out. Defaults to "Otvori karton". */
  akcija?: string;
}

/**
 * The server's gaps as rows in this list (O-18).
 *
 * They belong here rather than in a list of their own: the point of "Zahtijeva
 * pažnju" is that somebody reads one place and the ordering is done for them,
 * and a missing day competes for the same attention as an expiring permit.
 *
 * `dana` is negated because a gap is time already lost while every other kind
 * here is time remaining. That puts the oldest gap where it belongs — above a
 * deadline that has not arrived yet — and makes the badge read "prije 5 d.".
 */
export function rupeUStavke(rupe: RupaZaPaznju[]): Stavka[] {
  return rupe.map((rupa) => ({
    radnikId: rupa.gradiliste_id,
    radnik: rupa.gradiliste,
    vrsta: "evidencija" as const,
    poruka: [
      opisRupe(rupa),
      rupa.voditelj ? `voditelj ${rupa.voditelj}` : "gradilište nema voditelja",
      `${rupa.bez_evidencije} od ${rupa.na_rasporedu} radnika`,
    ].join(" · "),
    dana: -rupa.dana,
    // Past the seventh day it is a breach of NN 55/24 čl. 15, and coloured as
    // one. Before that it is a reminder.
    blokira: rupa.prekrsaj,
    poveznica: `/sati?datum=${rupa.datum}&gradiliste=${rupa.gradiliste_id}`,
    akcija: "Otvori dan",
  }));
}

const REDOSLIJED_BEZ_DATUMA = 9999;

export function stavkeZaPaznju(radnici: Radnik[], danas = new Date()): Stavka[] {
  const stavke: Stavka[] = [];

  for (const radnik of radnici) {
    if (!radnik.id) continue;
    const ime = punoIme(radnik);

    const dozvola = radnik.dozvola_status;
    if (dozvola && dozvola.stanje !== "nije_potrebno") {
      if (dozvola.stanje === "nema") {
        stavke.push({
          radnikId: radnik.id,
          radnik: ime,
          vrsta: "dozvola",
          poruka: "Nema zabilježene dozvole za boravak i rad",
          dana: null,
          blokira: true,
        });
      } else if (dozvola.stanje === "isteklo") {
        stavke.push({
          radnikId: radnik.id,
          radnik: ime,
          vrsta: "dozvola",
          poruka: "Dozvola je istekla — ne smije na gradilište",
          dana: dozvola.dana_do_isteka,
          blokira: true,
        });
      } else if (
        dozvola.stanje === "istice" &&
        (dozvola.dana_do_isteka ?? REDOSLIJED_BEZ_DATUMA) <=
          pragUpozorenja(radnik.vrsta_ugovora)
      ) {
        stavke.push({
          radnikId: radnik.id,
          radnik: ime,
          vrsta: "dozvola",
          poruka: "Dozvola uskoro ističe — pokrenuti produljenje",
          dana: dozvola.dana_do_isteka,
          blokira: false,
        });
      }
    }

    const zdravlje = radnik.zdravstveni_status;
    if (zdravlje) {
      if (!zdravlje.smije_raditi) {
        stavke.push({
          radnikId: radnik.id,
          radnik: ime,
          vrsta: "lijecnicki",
          poruka: "Liječnički: ne smije raditi",
          dana: null,
          blokira: true,
        });
      } else if (zdravlje.vrijedi_do) {
        const dana = danaDo(zdravlje.vrijedi_do, danas);
        if (dana < 0) {
          stavke.push({
            radnikId: radnik.id,
            radnik: ime,
            vrsta: "lijecnicki",
            poruka: "Liječnički pregled je istekao",
            dana,
            blokira: true,
          });
        } else if (dana <= PRAG_UPOZORENJA) {
          stavke.push({
            radnikId: radnik.id,
            radnik: ime,
            vrsta: "lijecnicki",
            poruka: "Liječnički pregled uskoro ističe",
            dana,
            blokira: false,
          });
        }
      }
    }

    if (radnik.ugovor_vrijedi_do) {
      const dana = danaDo(radnik.ugovor_vrijedi_do, danas);
      if (dana <= PRAG_UPOZORENJA) {
        stavke.push({
          radnikId: radnik.id,
          radnik: ime,
          vrsta: "ugovor",
          poruka:
            dana < 0 ? "Ugovor je istekao" : "Ugovor na određeno uskoro ističe",
          dana,
          blokira: false,
        });
      }
    }

    const oprema = radnik.oprema_status;
    if (oprema?.dana_do_roka !== null && oprema?.dana_do_roka !== undefined) {
      if (oprema.dana_do_roka <= PRAG_UPOZORENJA) {
        stavke.push({
          radnikId: radnik.id,
          radnik: ime,
          vrsta: "oprema",
          poruka:
            oprema.dana_do_roka < 0
              ? "Zaštitnoj opremi je prošao rok zamjene"
              : "Zaštitnoj opremi uskoro ističe rok zamjene",
          dana: oprema.dana_do_roka,
          blokira: false,
        });
      }
    }

    if (radnik.uskladenost && !radnik.uskladenost.potpuno) {
      stavke.push({
        radnikId: radnik.id,
        radnik: ime,
        vrsta: "profil",
        poruka: `Nedostaje: ${radnik.uskladenost.nedostaje.join(", ")}`,
        dana: null,
        blokira: false,
      });
    }
  }

  return stavke.sort((a, b) => {
    if (a.blokira !== b.blokira) return a.blokira ? -1 : 1;
    const da = a.dana ?? REDOSLIJED_BEZ_DATUMA;
    const db = b.dana ?? REDOSLIJED_BEZ_DATUMA;
    return da - db;
  });
}

/** The four counters across the top of the dashboard. */
export interface Brojevi {
  aktivni: number;
  upozorenja: number;
  kriticno: number;
  nepotpuni: number;
}

export function prebroj(radnici: Radnik[], danas = new Date()): Brojevi {
  const stavke = stavkeZaPaznju(radnici, danas);
  const blokirani = new Set(
    stavke.filter((s) => s.blokira).map((s) => s.radnikId),
  );
  const upozoreni = new Set(
    stavke
      .filter((s) => !s.blokira && s.vrsta !== "profil")
      .map((s) => s.radnikId),
  );

  return {
    aktivni: radnici.filter((r) => r.aktivan !== false).length,
    // A blocked worker is counted as critical, not as a warning as well: two
    // counters that add up to more than the workforce teach people to ignore
    // both.
    upozorenja: [...upozoreni].filter((id) => !blokirani.has(id)).length,
    kriticno: blokirani.size,
    nepotpuni: radnici.filter((r) => r.uskladenost && !r.uskladenost.potpuno)
      .length,
  };
}

export { PRAG_ESKALACIJE };
